import type { LookupItem, Role } from '@jmc/shared';
import { formatDateTime, ROLE_LABELS, ROLES } from '@jmc/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { api, ApiRequestError } from '../lib/api.js';
import { useAuth } from '../lib/auth.js';
import { useI18n } from '../lib/i18n.js';
import {
  ConfirmDialog,
  cx,
  ErrorState,
  Field,
  InlineError,
  Modal,
  Mono,
  PageHeader,
  Panel,
  Select,
  Spinner,
  TableSkeleton,
  TextInput,
  Toast,
} from '../components/ui.js';

interface UserRow {
  id: number;
  username: string;
  fullName: string;
  fullNameHi: string | null;
  mobileMasked: string;
  email: string | null;
  designation: string | null;
  role: Role;
  departmentName: string | null;
  isActive: boolean;
  lastLoginAt: string | null;
  openFiles: number;
}

export function UsersPage() {
  const { t, pick } = useI18n();
  const { user: currentUser, can } = useAuth();
  const queryClient = useQueryClient();

  const [showInactive, setShowInactive] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [credential, setCredential] = useState<{ name: string; password: string } | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Destructive actions wait for confirmation. Reactivating is not
  // destructive, so it is not held up by a dialog.
  const [pending, setPending] = useState<
    { action: 'deactivate' | 'reset'; row: UserRow } | null
  >(null);

  const users = useQuery({
    queryKey: ['users', showInactive],
    queryFn: () => api<{ users: UserRow[] }>(`/users?includeInactive=${showInactive}`),
  });

  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['users'] });

  const toggleActive = useMutation({
    mutationFn: (row: UserRow) =>
      api(`/users/${row.id}`, { method: 'PATCH', body: { isActive: !row.isActive } }),
    onSuccess: (_result, row) => {
      setError(null);
      setPending(null);
      setToast(
        row.isActive
          ? `${row.fullName} — ${t('users.inactive')}`
          : `${row.fullName} — ${t('users.active')}`,
      );
      refresh();
    },
    onError: (caught) => {
      setPending(null);
      setError(caught instanceof ApiRequestError ? caught.message : t('common.error'));
    },
  });

  const resetPassword = useMutation({
    mutationFn: (row: UserRow) =>
      api<{ temporaryPassword: string }>(`/users/${row.id}/reset-password`, { method: 'POST' }),
    onSuccess: (result, row) => {
      setPending(null);
      setCredential({ name: row.fullName, password: result.temporaryPassword });
    },
    onError: (caught) => {
      setPending(null);
      setError(caught instanceof ApiRequestError ? caught.message : t('common.error'));
    },
  });

  const runPending = () => {
    if (!pending) return;
    if (pending.action === 'reset') resetPassword.mutate(pending.row);
    else toggleActive.mutate(pending.row);
  };

  return (
    <>
      <PageHeader
        title={t('users.title')}
        actions={
          <>
            <label className="flex items-center gap-2 text-[0.8125rem]">
              <input
                type="checkbox"
                checked={showInactive}
                onChange={(event) => setShowInactive(event.target.checked)}
                className="h-4 w-4 rounded border-line-strong"
              />
              {t('users.showInactive')}
            </label>
            {can('user:manage') && (
              <button type="button" className="btn-primary" onClick={() => setAddOpen(true)}>
                {t('users.add')}
              </button>
            )}
          </>
        }
      />

      {error && <div className="mb-4"><InlineError message={error} /></div>}

      <Panel bodyClassName="overflow-x-auto">
        {users.isLoading && <TableSkeleton columns={6} />}
        {users.isError && (
          <div className="p-4">
            <ErrorState error={users.error} onRetry={() => void users.refetch()} />
          </div>
        )}
        {/* Narrow screens get stacked rows; the actions column clips in a table. */}
        {users.data && (
          <ul className="divide-y divide-line lg:hidden">
            {users.data.users.map((row) => (
              <li key={row.id} className={cx('px-4 py-3', !row.isActive && 'opacity-60')}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="font-medium">{pick(row.fullName, row.fullNameHi)}</p>
                    <p className="text-[0.8125rem] text-ink-500">
                      @{row.username}
                      {row.designation ? ` · ${row.designation}` : ''}
                    </p>
                  </div>
                  <span className="shrink-0 text-[0.8125rem] text-ink-500">
                    {pick(ROLE_LABELS[row.role].en, ROLE_LABELS[row.role].hi)}
                  </span>
                </div>
                <p className="mt-1 text-[0.8125rem] text-ink-500">
                  <Mono>{row.mobileMasked}</Mono>
                  {row.openFiles > 0 && (
                    <span className="ml-3 font-medium text-navy-700">
                      {row.openFiles} {t('users.openFiles').toLowerCase()}
                    </span>
                  )}
                  {!row.isActive && <span className="ml-3">{t('users.inactive')}</span>}
                </p>
                {can('user:manage') && row.id !== currentUser?.id && (
                  <div className="mt-2 flex flex-wrap gap-2">
                    <button
                      type="button"
                      className="btn-secondary px-2.5 py-1.5"
                      onClick={() => setPending({ action: 'reset', row })}
                    >
                      {t('users.resetPassword')}
                    </button>
                    <button
                      type="button"
                      className="btn-secondary px-2.5 py-1.5"
                      onClick={() =>
                        row.isActive
                          ? setPending({ action: 'deactivate', row })
                          : toggleActive.mutate(row)
                      }
                    >
                      {row.isActive ? t('users.deactivate') : t('users.activate')}
                    </button>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}

        {users.data && (
          <table className="data-table hidden lg:table">
            <thead>
              <tr>
                <th>{t('users.name')}</th>
                <th>{t('users.role')}</th>
                <th className="hidden md:table-cell">{t('users.mobile')}</th>
                <th className="hidden lg:table-cell">{t('users.openFiles')}</th>
                <th className="hidden lg:table-cell">{t('users.lastLogin')}</th>
                {can('user:manage') && <th />}
              </tr>
            </thead>
            <tbody>
              {users.data.users.map((row) => (
                <tr key={row.id} className={cx(!row.isActive && 'opacity-60')}>
                  <td>
                    <div className="font-medium">
                      {pick(row.fullName, row.fullNameHi)}
                      {!row.isActive && (
                        <span className="ml-2 badge border-line-strong bg-paper text-ink-500">
                          {t('users.inactive')}
                        </span>
                      )}
                    </div>
                    <div className="text-[0.8125rem] text-ink-500">
                      @{row.username}
                      {row.designation ? ` · ${row.designation}` : ''}
                    </div>
                  </td>
                  <td>{pick(ROLE_LABELS[row.role].en, ROLE_LABELS[row.role].hi)}</td>
                  <td className="hidden md:table-cell">
                    <Mono>{row.mobileMasked}</Mono>
                  </td>
                  <td className="hidden tabular lg:table-cell">
                    {row.openFiles > 0 ? (
                      <span className="font-medium text-navy-700">{row.openFiles}</span>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td className="hidden whitespace-nowrap lg:table-cell text-ink-500">
                    {row.lastLoginAt ? formatDateTime(row.lastLoginAt) : '—'}
                  </td>
                  {can('user:manage') && (
                    <td className="whitespace-nowrap text-right">
                      {row.id !== currentUser?.id && (
                        <>
                          <button
                            type="button"
                            className="btn-ghost px-2 py-1"
                            onClick={() => setPending({ action: 'reset', row })}
                          >
                            {t('users.resetPassword')}
                          </button>
                          <button
                            type="button"
                            className="btn-ghost px-2 py-1"
                            onClick={() =>
                              row.isActive
                                ? setPending({ action: 'deactivate', row })
                                : toggleActive.mutate(row)
                            }
                          >
                            {row.isActive ? t('users.deactivate') : t('users.activate')}
                          </button>
                        </>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Panel>

      {addOpen && (
        <AddUserDialog
          onClose={() => setAddOpen(false)}
          onCreated={(name, password) => {
            setAddOpen(false);
            setCredential({ name, password });
            refresh();
          }}
        />
      )}

      {credential && (
        <Modal
          open
          title={t('users.tempPassword')}
          onClose={() => setCredential(null)}
          footer={
            <button type="button" className="btn-primary" onClick={() => setCredential(null)}>
              {t('action.close')}
            </button>
          }
        >
          <p className="mb-3">{credential.name}</p>
          <p className="rounded-[5px] border border-line-strong bg-paper px-4 py-3 text-center">
            <Mono className="text-lg font-semibold tracking-wider">{credential.password}</Mono>
          </p>
          <p className="mt-3 rounded-[5px] border border-amber-line bg-amber-bg px-3 py-2 text-[0.8125rem] text-amber-ink">
            {t('users.tempPasswordHint')}
          </p>
        </Modal>
      )}

      <ConfirmDialog
        open={pending !== null}
        title={t('confirm.title')}
        message={
          pending?.action === 'reset'
            ? t('confirm.resetPassword', { name: pending.row.fullName })
            : pending
              ? t('confirm.deactivate', { name: pending.row.fullName })
              : ''
        }
        confirmLabel={t('confirm.proceed')}
        tone="danger"
        busy={toggleActive.isPending || resetPassword.isPending}
        onConfirm={runPending}
        onCancel={() => setPending(null)}
      />

      {toast && <Toast message={toast} onDismiss={() => setToast(null)} />}
    </>
  );
}

function AddUserDialog({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (name: string, password: string) => void;
}) {
  const { t, pick } = useI18n();
  const [form, setForm] = useState({
    username: '',
    fullName: '',
    fullNameHi: '',
    mobile: '',
    designation: '',
    employeeCode: '',
    role: 'VIEWER' as Role,
    departmentId: '',
  });
  const [fields, setFields] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);

  const lookups = useQuery({
    queryKey: ['lookups'],
    queryFn: () => api<{ departments: LookupItem[]; zones: LookupItem[] }>('/files/lookups'),
  });

  const create = useMutation({
    mutationFn: () =>
      api<{ user: { fullName: string }; temporaryPassword: string }>('/users', {
        method: 'POST',
        body: {
          ...form,
          fullNameHi: form.fullNameHi || null,
          designation: form.designation || null,
          employeeCode: form.employeeCode || null,
          departmentId: form.departmentId ? Number(form.departmentId) : null,
        },
      }),
    onSuccess: (result) => onCreated(result.user.fullName, result.temporaryPassword),
    onError: (caught) => {
      if (caught instanceof ApiRequestError) {
        setError(caught.message);
        setFields(caught.fields);
      } else setError(t('common.error'));
    },
  });

  const set = (key: keyof typeof form) => (value: string) =>
    setForm((current) => ({ ...current, [key]: value }));

  return (
    <Modal
      open
      title={t('users.add')}
      onClose={onClose}
      width="max-w-2xl"
      footer={
        <>
          <button type="button" className="btn-secondary" onClick={onClose}>
            {t('action.cancel')}
          </button>
          <button type="submit" form="add-user" className="btn-primary" disabled={create.isPending}>
            {create.isPending && <Spinner />}
            {t('action.save')}
          </button>
        </>
      }
    >
      {error && <div className="mb-4"><InlineError message={error} /></div>}

      <form
        id="add-user"
        className="grid gap-4 sm:grid-cols-2"
        onSubmit={(event: FormEvent) => {
          event.preventDefault();
          create.mutate();
        }}
      >
        <Field label={t('users.name')} error={fields['fullName']} required>
          <TextInput
            value={form.fullName}
            onChange={(event) => set('fullName')(event.target.value)}
            required
            autoFocus
          />
        </Field>

        <Field label={`${t('users.name')} (हिंदी)`} error={fields['fullNameHi']}>
          <TextInput
            value={form.fullNameHi}
            onChange={(event) => set('fullNameHi')(event.target.value)}
            lang="hi"
          />
        </Field>

        <Field label={t('users.username')} error={fields['username']} required>
          <TextInput
            value={form.username}
            onChange={(event) => set('username')(event.target.value)}
            required
            autoComplete="off"
          />
        </Field>

        <Field
          label={t('users.mobile')}
          error={fields['mobile']}
          hint="10 digits — the OTP goes here"
          required
        >
          <TextInput
            value={form.mobile}
            onChange={(event) => set('mobile')(event.target.value)}
            inputMode="numeric"
            maxLength={13}
            required
          />
        </Field>

        <Field label={t('users.role')} error={fields['role']} required>
          <Select value={form.role} onChange={(event) => set('role')(event.target.value)}>
            {ROLES.map((role) => (
              <option key={role} value={role}>
                {pick(ROLE_LABELS[role].en, ROLE_LABELS[role].hi)}
              </option>
            ))}
          </Select>
        </Field>

        <Field label={t('field.department')} error={fields['departmentId']}>
          <Select
            value={form.departmentId}
            onChange={(event) => set('departmentId')(event.target.value)}
          >
            <option value="">{t('common.none')}</option>
            {lookups.data?.departments.map((department) => (
              <option key={department.id} value={department.id}>
                {pick(department.nameEn, department.nameHi)}
              </option>
            ))}
          </Select>
        </Field>

        <Field label={t('users.designation')} error={fields['designation']}>
          <TextInput
            value={form.designation}
            onChange={(event) => set('designation')(event.target.value)}
          />
        </Field>

        <Field label="Employee code" error={fields['employeeCode']}>
          <TextInput
            value={form.employeeCode}
            onChange={(event) => set('employeeCode')(event.target.value)}
          />
        </Field>
      </form>
    </Modal>
  );
}
